// hooks/usePhotoEditor.js - Main hook for the photo editor
import { useCallback } from 'react';
import { usePhotoEditorContext } from '../context/PhotoEditorContext';
import { exportCanvasToBlob, exportCanvasToDataURL } from '../utils/photoEditor';
import { blobToFile } from '../utils/helpers';

const usePhotoEditor = () => {
  const {
    originalSizeCanvasRef,
    previewCanvasRef,
    outputMime = 'image/jpeg',
    adjustments,
    transformations
  } = usePhotoEditorContext();

  // Export the edited image as a blob
  const exportToBlob = useCallback(async () => {
    const canvas = originalSizeCanvasRef.current;
    if (!canvas) return null;
    
    try {
      return await exportCanvasToBlob(canvas, outputMime);
    } catch (error) {
      console.error('Error exporting to blob:', error);
      return null;
    }
  }, [originalSizeCanvasRef, outputMime]);

  // Export the edited image as a data URL
  const exportToDataURL = useCallback(() => {
    const canvas = originalSizeCanvasRef.current;
    if (!canvas) return null;
    
    try {
      return exportCanvasToDataURL(canvas, outputMime);
    } catch (error) {
      console.error('Error exporting to data URL:', error);
      return null;
    }
  }, [originalSizeCanvasRef, outputMime]);

  // Export the edited image as a file
  const exportToFile = useCallback(async (fileName = 'edited-image') => {
    const blob = await exportToBlob();
    if (!blob) return null;
    
    // Add file extension based on mime type
    const extension = outputMime.split('/')[1];
    const fullFileName = `${fileName}.${extension}`;
    
    return blobToFile(blob, fullFileName);
  }, [exportToBlob, outputMime]);

  // Reset all adjustments and transformations
  const resetEdits = useCallback(() => {
    // This would need to reload the original image and reset all state
    // Implementation would depend on how the app manages state resets
    console.log('Reset edits functionality would be implemented here');
  }, []);

  return {
    exportToBlob,
    exportToDataURL,
    exportToFile,
    resetEdits,
    adjustments,
    transformations
  };
};

export default usePhotoEditor;
